import PropTypes from 'prop-types';
import SingleBetImpl from './single-bet.js';
import MojitoCore from 'mojito/core';
import { noop } from 'mojito/utils';

const UIView = MojitoCore.Presentation.UIView;

/**
 * Single bet view.
 * For proper analytics reporting this component should be within the scope
 * of {@link Mojito.Core.Presentation.AnalyticsContext.Context|AnalyticsContext} with a defined <code>analyticsPath</code>.
 * This will provide additional information about the component's position in an analytics aware hierarchy, e.g., "Betslip".
 *
 * @class SingleBet
 * @memberof Mojito.Modules.BetslipView
 */

/**
 * SingleBet prop types.
 *
 * @property {Mojito.Services.Betslip.types.Bet} bet - The bet.
 * @property {boolean} [showStake] - Flag indicating if the stake input should be displayed.
 * @property {string} [currencyCode] - The code of the currency used to format currency values.
 * @property {boolean} [suspended = false] - Flag indicating whether the bet is suspended.
 * @property {boolean} [expired = false] - Flag indicating whether the bet is expired.
 * @property {Function} [cbRemoveBet = ()=>{}] - Callback triggered when a bet is removed from the slip.
 * @property {boolean} [highlight = false] - Highlights the bet if set to true.
 * @property {Mojito.Services.Betslip.types.BetStake} [betStake = {}] - Stake information for the bet.
 * @property {boolean} [hasOddsChange = false] - Flag indicating if bet odds have changed.
 * @property {boolean} [hasHcapChange = false] - Flag indicating if there are handicap changes in the bet.
 * @property {boolean} [showEachWay = false] - Controls the visibility of the 'each way' toggle button.
 * @property {Mojito.Services.Betslip.types.BETSLIP_STATUS} [betslipStatus] - Betslip status.
 * @property {boolean} [showPotentialReturns = true] - Determines whether to display the label for potential returns.
 * @property {boolean} [forceEWLabelsToShow = false] - Determines whether to display the each-way label.
 * @property {Function} [onStakeInputFocus = () => {}] - Callback triggered when the stake input gains focus.
 * @property {Function} [onSelectionInfoClick = () => {}] - Callback triggered after click on selection name, market name or event name.
 * @property {boolean} [showBanker = false] - Controls the visibility of the 'banker' toggle button.
 * @property {boolean} [isBankerPending = false] - Indicates whether the setting of a banker bet is in progress.
 * @property {Mojito.Services.Betslip.types.Betslip} [betslip = {}] - The betslip.
 * @property {object} [suspendedParts = {}] - Map of suspended parts of the bet.
 * @property {boolean} [isTeaser = false] - Boolean value indicating the current stake group is the teaser group.
 *
 * @memberof Mojito.Modules.BetslipView.SingleBet
 */
const propTypes = {
    bet: PropTypes.object.isRequired,
    showStake: PropTypes.bool,
    currencyCode: PropTypes.string,
    suspended: PropTypes.bool,
    expired: PropTypes.bool,
    cbRemoveBet: PropTypes.func,
    highlight: PropTypes.bool,
    betStake: PropTypes.object,
    hasOddsChange: PropTypes.bool,
    hasHcapChange: PropTypes.bool,
    showEachWay: PropTypes.bool,
    betslipStatus: PropTypes.string,
    showPotentialReturns: PropTypes.bool,
    forceEWLabelsToShow: PropTypes.bool,
    onStakeInputFocus: PropTypes.func,
    onSelectionInfoClick: PropTypes.func,
    showBanker: PropTypes.bool,
    isBankerPending: PropTypes.bool,
    betslip: PropTypes.object,
    suspendedParts: PropTypes.objectOf(
        PropTypes.shape({
            isSuspended: PropTypes.bool,
        })
    ),
    isTeaser: PropTypes.bool,
    hasPendingBonus: PropTypes.bool,
};

const defaultProps = {
    suspended: false,
    expired: false,
    showStake: false,
    cbRemoveBet: noop,
    highlight: false,
    betStake: {},
    hasOddsChange: false,
    hasHcapChange: false,
    showEachWay: false,
    showPotentialReturns: true,
    forceEWLabelsToShow: false,
    onStakeInputFocus: noop,
    onSelectionInfoClick: noop,
    showBanker: false,
    isBankerPending: false,
    betslip: {},
    suspendedParts: {},
    isTeaser: false,
    hasPendingBonus: false,
};

export default UIView('SingleBet', SingleBetImpl, propTypes, defaultProps);
